import { FunctionComponent, useCallback, useEffect, useMemo, useRef } from 'react'
import TablePageNavigation from '../../../../components/TablePageNavigation'
import Row from '../../../../components/Row'
import FilterButton from '../../../../fragments/FilterButton'
import { SidebarTypes } from '../../../../interfaces/store/appStore'
import Button from '../../../../components/Button'
import { useTasksStore } from '../../../../stores/tasks/useTasksStore'
import { useTasksHandlers } from '../../../../services/actions/tasks/useTasksHandlers'
import { useAppHandlers } from '../../../../services/actions/app/useAppHandlers'
import TasksBulkActionsDropdown from '../../../../fragments/dropdowns/TasksBulkActionsDropdown'
import styled from 'styled-components'

interface ITaskControlBarProps {
    selectedTasks: any[],
}

const TaskControlBar: FunctionComponent<ITaskControlBarProps> = ({selectedTasks}) => {
    const { store: { tasksParameters, tasksFilters } } = useTasksStore()
    const { openSidebar } = useAppHandlers()
    const { setTasksParametersHandler, numberOfTasksFiltersHandler, bulkUpdateTasksHandler, setClearTasksFilterHandler, getAllTasksHandler } = useTasksHandlers()

    // Memoize the stringified filters to avoid unnecessary re-fetches
    const filtersKey = useMemo(() => JSON.stringify(tasksFilters), [tasksFilters])
    const paginationKey = useMemo(() => 
        `${tasksParameters?.page}-${tasksParameters?.pageSize}-${tasksParameters?.sort}`, 
        [tasksParameters?.page, tasksParameters?.pageSize, tasksParameters?.sort]
    )

    // Track previous keys to prevent unnecessary refetches
    const prevFiltersKey = useRef<string>()
    const prevPaginationKey = useRef<string>()

    // Only fetch tasks when filters or pagination parameters actually change
    useEffect(() => {
        // On first mount, always fetch
        const isFirstMount = prevFiltersKey.current === undefined && prevPaginationKey.current === undefined;
        
        // Check if values actually changed
        const filtersChanged = prevFiltersKey.current !== filtersKey;
        const paginationChanged = prevPaginationKey.current !== paginationKey;
        
        // Only fetch if it's first mount OR if keys actually changed
        if (isFirstMount || filtersChanged || paginationChanged) {
            getAllTasksHandler();
        }
        
        // Update refs after checking
        prevFiltersKey.current = filtersKey;
        prevPaginationKey.current = paginationKey;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtersKey, paginationKey])

    const resetFilterHandler = useCallback(() => {
        setClearTasksFilterHandler();
    }, [setClearTasksFilterHandler]);

    return <Row gap='20px' alignItems spaceBetween flexWrap>
        <Row gap="12px">
            <Button $type='blue' $bigButton onClickHandler={() => openSidebar(SidebarTypes.TASK_EDIT_CREATE)}>{'Add new task'}</Button>
            <HideMobile>
                <Button $type='white' disabled={selectedTasks.length === 0} $bigButton $SVGtype='checkmark' onClickHandler={() => bulkUpdateTasksHandler({ isBulkComplete: true, ids: selectedTasks })}>{'Mark Complete'}</Button>
            </HideMobile>
            <TasksBulkActionsDropdown disabled={selectedTasks.length === 0} selectedTasks={selectedTasks} />
        </Row>
        <Row gap='12px'>
            <TablePageNavigation
                page={tasksParameters.page ?? 1}
                pageSize={tasksParameters.pageSize ?? 25}
                totalCount={tasksParameters?.total ?? 0}
                onPageChanged={(value: number) => setTasksParametersHandler({...tasksParameters, page: value})}
                onPageSizeChanged={(value: number) => setTasksParametersHandler({...tasksParameters, pageSize: value, page: '1'})}
            />
            <FilterButton 
                numberOfFilters={numberOfTasksFiltersHandler()} 
                sidebar={SidebarTypes.TASKS_FILTER}
                resetFilter={() => resetFilterHandler()}/>
        </Row>
    </Row>
}
const HideMobile = styled.div`
    @media (max-width: 575px) {
        display: none;
    }
`
export default TaskControlBar